import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaShieldAlt, FaMobile, FaGlobe, FaEnvelope } from 'react-icons/fa';
import { Link, useLocation } from 'react-router-dom';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
    const location = useLocation();
    
    useEffect(() => {
        if (location.hash) {
            // Remove the leading '#' and decode URI
            const id = decodeURIComponent(location.hash.replace('#', ''));
            const el = document.getElementById(id);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
    }, [location]);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                delayChildren: 0.2,
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: {
                duration: 0.5
            }
        }
    };

    return (
        <div className="privacy-policy">
            <div className="container">
                <motion.div 
                    className="privacy-policy__header"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    <Link to="/" className="back-link">
                        <FaArrowLeft />
                        <span>Back to Home</span>
                    </Link>
                    
                    <div className="section-badge">
                        <FaShieldAlt />
                        Legal Information
                    </div>
                    <h1 className="page-title">
                        Privacy <span className="title-gradient">Policy</span>
                    </h1>
                    <p className="page-description">
                        Your privacy is important to us. This policy explains how we handle information 
                        across our website and applications.
                    </p>
                </motion.div>

                <motion.div 
                    className="privacy-policy__content"
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                >
                    {/* General Website Privacy Policy */}
                    <motion.section className="privacy-section" variants={itemVariants}>
                        <div className="section-icon">
                            <FaGlobe />
                        </div>
                        <h2>Website Privacy Policy</h2>
                        <div className="policy-content">
                            <div className="policy-item">
                                <h3>Information We Collect</h3>
                                <p>
                                    This portfolio website is designed with privacy in mind. We do not collect, 
                                    store, or process any personal information from visitors. The website operates 
                                    as a static site hosted on GitHub Pages.
                                </p>
                                <ul>
                                    <li>No cookies are used for tracking purposes</li>
                                    <li>No analytics or tracking scripts are implemented</li>
                                    <li>No user accounts or registration required</li>
                                    <li>Contact form submissions are handled through third-party services with their own privacy policies</li>
                                </ul>
                            </div>

                            <div className="policy-item">
                                <h3>Third-Party Services</h3>
                                <p>
                                    This website may use third-party services for contact form functionality. 
                                    Any information you provide through contact forms is subject to the privacy 
                                    policies of those respective services.
                                </p>
                            </div>

                            <div className="policy-item">
                                <h3>External Links</h3>
                                <p>
                                    Our website may contain links to external sites and applications. We are not 
                                    responsible for the privacy practices of these external sites. We encourage 
                                    you to review their privacy policies before use.
                                </p>
                            </div>
                        </div>
                    </motion.section>

                    {/* Ingredients Cost App Privacy Policy */}
                    <motion.section className="privacy-section app-section" variants={itemVariants} id="ingredients-cost-app-privacy-policy">
                        <div className="section-icon">
                            <FaMobile />
                        </div>
                        <h2>Ingredients Cost App Privacy Policy</h2>
                        <div className="app-header">
                            <h3>تكلفة المنتجات (Ingredients Cost App)</h3>
                            <p className="effective-date">Effective Date: June 28, 2025</p>
                            <p className="developer">Developer: 4onetwodev</p>
                        </div>
                        
                        <div className="policy-content">
                            <div className="policy-item">
                                <h4>1. Information We Collect</h4>
                                <p><strong>We do not collect, store, or share any personal information from users of this app.</strong></p>
                                <ul>
                                    <li>The app does not require you to create an account or provide any personal details</li>
                                    <li>The app does not collect location data, contacts, photos, or any other personal data</li>
                                    <li>All data you enter (such as product names, costs, or recipes) is stored locally on your device and is not transmitted to us or any third party</li>
                                </ul>
                            </div>

                            <div className="policy-item">
                                <h4>2. Permissions</h4>
                                <p>The app may request permission to access device storage. This is only used to:</p>
                                <ul>
                                    <li>Export or import your data (such as CSV files) at your request</li>
                                    <li>The app does not access, read, or modify any other files on your device</li>
                                </ul>
                            </div>

                            <div className="policy-item">
                                <h4>3. Third-Party Services</h4>
                                <p>This app does not use third-party analytics, advertising, or tracking services.</p>
                            </div>

                            <div className="policy-item">
                                <h4>4. Data Security</h4>
                                <ul>
                                    <li>All your data is stored locally on your device. We do not have access to your data</li>
                                    <li>If you uninstall the app, your data will be deleted from your device</li>
                                </ul>
                            </div>

                            <div className="policy-item">
                                <h4>5. Children's Privacy</h4>
                                <p>This app is suitable for all ages and does not knowingly collect information from children.</p>
                            </div>

                            <div className="policy-item">
                                <h4>6. Changes to This Policy</h4>
                                <p>We may update this privacy policy from time to time. Any changes will be posted on this page.</p>
                            </div>
                        </div>
                    </motion.section>

                    {/* Contact Section */}
                    <motion.section className="privacy-section contact-section" variants={itemVariants}>
                        <div className="section-icon">
                            <FaEnvelope />
                        </div>
                        <h2>Contact Us</h2>
                        <div className="policy-content">
                            <p>
                                If you have any questions about this privacy policy or our privacy practices, 
                                please contact us at:
                            </p>
                            <div className="contact-info">
                                <a href="mailto:contact@4onetwodev.uk" className="contact-link">
                                    <FaEnvelope />
                                    contact@4onetwodev.uk
                                </a>
                            </div>
                        </div>
                    </motion.section>

                    {/* Last Updated */}
                    <motion.div className="privacy-footer" variants={itemVariants}>
                        <p>Last Updated: June 28, 2025</p>
                    </motion.div>
                </motion.div>
            </div>
        </div>
    );
};

export default PrivacyPolicy;
